import React, { useState, useEffect, useMemo } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Button, Row, Col } from 'reactstrap'

import TaskCheckList from './TaskCheckList'

import CompleteTaskForm from './CompleteTaskForm'
import PopupModal from '../../common/CustomPopupModal/CustomPopupModal'
import CompleteTaskContactInfo from './CompleteTaskContactInfo'
import ContactInfoForTask from './ContactInfoForTask'
import CompleteTaskAdditionalContact from './CompleteTaskAdditionalContact'
import Loader from '../Loader/BasicLoader'

import { completeTask, getNotesForTask, resetAllData, setIsLoading } from './Redux/taskActions'
import { setModal, closeModal } from '../../common/GlobalModal/redux/GlobalModalActions.js'
import { clearExpenseData } from '../Expenses/redux/expenseActions'
import isEmpty from '../../utils/isEmpty'
import { clearAllErrors } from '../../actions/errorsActions'
import ActivitiesTimeline from '../../common/ContactCommonComponents/ActivitiesTimeline/ActivitiesTimeline'
import { EMAIL, OUT_GOING_CALL } from '../../constants/activityTypes'
import { LOGISTICS } from '../../constants/divisions'
import { updateCustomerLogistics } from '../CustomerLogistics/redux/customerLogisticsActions.js'
import { SHIPPERS } from '../../constants/contactCategory.js'
import { ACCOUNT_STATUS, PENDING_STATUS, PROSPECT_STATUS } from '../../constants/statuses.js'
import moment from 'moment'
import { getContact, updateContactDivision } from '../Contacts/redux/ContactActions.js'

const CompleteTask = ({
  isModal,
  completeTask,
  taskId,
  closeModal,
  getNotesForTask,
  contactId,
  taskStatus,
  isLoading,
  contact,
  divisionId,
  errors,
  isLogisticsCustomerProspect,
  isLogisticsProspectOrAccountCustomer,
  isDashboardAction,
  updateCustomerLogistics,
  isLogisticsCustomerTaskActionFromDashboard,
  contactIdTask, // contact id for dashboard actions
  isRfp,
  isMfp,
  isCreditApp,
  isOnlineForm,
}) => {
  const dispatch = useDispatch()

  const [isRfpCustomer, setIsRfpCustomer] = useState(false)
  const [isMfpCustomer, setIsMfpCustomer] = useState(false)
  const [isCreditAppCustomer, setIsCreditAppCustomer] = useState(false)
  const [isOnlineFormCustomer, setIsOnlineFormCustomer] = useState(false)
  const [isOpen, setIsOpen] = useState(true)
  const [ratingData, setRatingData] = useState({ grade: null, effortLevel: null })

  const displayButton = useMemo(() => {
    return {
      rfp: (isLogisticsCustomerProspect && !isDashboardAction) || isLogisticsCustomerTaskActionFromDashboard,
      mfp: (isLogisticsProspectOrAccountCustomer && !isDashboardAction) || isLogisticsCustomerTaskActionFromDashboard,
      creditApp:
        (isLogisticsProspectOrAccountCustomer && !isDashboardAction) || isLogisticsCustomerTaskActionFromDashboard,
      onlineForm:
        (isLogisticsProspectOrAccountCustomer && !isDashboardAction) || isLogisticsCustomerTaskActionFromDashboard,
    }
  }, [
    isLogisticsCustomerProspect,
    isLogisticsCustomerTaskActionFromDashboard,
    isLogisticsProspectOrAccountCustomer,
    isDashboardAction,
  ])

  const [newActivityType, setNewActivityType] = useState(null)
  const [isCalled, setIsCalled] = useState(false)
  const [confirmSendEmailModal, setConfirmSendEmailModal] = useState(false)

  useEffect(() => {
    setIsRfpCustomer(() => {
      if (isLogisticsCustomerTaskActionFromDashboard) {
        return isRfp
      } else {
        return contact.isRfp
      }
    })

    setIsCreditAppCustomer(() => {
      if (isLogisticsCustomerTaskActionFromDashboard) {
        return isCreditApp
      } else {
        return contact.isCreditApp
      }
    })
  }, [contact.isCreditApp, isLogisticsCustomerTaskActionFromDashboard, isCreditApp])

  useEffect(() => {
    setIsOnlineFormCustomer(() => {
      if (isLogisticsCustomerTaskActionFromDashboard) {
        return isOnlineForm
      } else {
        return contact.isOnlineForm
      }
    })
  }, [contact.isOnlineForm, isLogisticsCustomerTaskActionFromDashboard, isOnlineForm])

  useEffect(() => {
    setIsMfpCustomer(() => {
      if (isLogisticsCustomerTaskActionFromDashboard) {
        return isMfp
      } else {
        return contact.isMfp
      }
    })
  }, [contact.isMfp, isLogisticsCustomerTaskActionFromDashboard, isMfp])

  useEffect(() => {
    return () => {
      dispatch(resetAllData())
      dispatch(clearExpenseData())
      dispatch(clearAllErrors())
    }
  }, [])

  useEffect(() => {
    if (contactId) {
      dispatch(getContact(contactId, divisionId))
    }
  }, [contactId])

  useEffect(() => {
    if (!isEmpty(errors)) {
      dispatch(setIsLoading())
    }
  }, [errors])

  const toggleConfirmSendEmailModal = () => {
    setConfirmSendEmailModal(!confirmSendEmailModal)
  }

  const openNextStep = (activityType, isCallMade) => {
    if (isCallMade) {
      setIsCalled(isCallMade)
    }
    setNewActivityType(activityType)
    setIsOpen(false)
  }

  useEffect(() => {
    getNotesForTask(contactId)
  }, [contactId])

  const handleUpdateCustomerLogistics = () => {
    const shouldUpdateRfp =
      (isLogisticsCustomerTaskActionFromDashboard && isRfp != isRfpCustomer) ||
      (isLogisticsCustomerProspect && isRfpCustomer != contact.isRfp && !isDashboardAction)

    const shouldUpdateMfp =
      (isLogisticsCustomerTaskActionFromDashboard && isMfp != isMfpCustomer) ||
      (isLogisticsProspectOrAccountCustomer && isMfpCustomer != contact.isMfp && !isDashboardAction)

    const shouldUpdateCreditApp =
      (isLogisticsCustomerTaskActionFromDashboard && isCreditApp != isCreditAppCustomer) ||
      (isLogisticsProspectOrAccountCustomer && isCreditAppCustomer != contact.isCreditApp && !isDashboardAction)

    const shouldUpdateOnlineForm =
      (isLogisticsCustomerTaskActionFromDashboard && isOnlineForm != isOnlineFormCustomer) ||
      (isLogisticsProspectOrAccountCustomer && isOnlineFormCustomer != contact.isOnlineForm && !isDashboardAction)

    if (shouldUpdateRfp || shouldUpdateMfp || shouldUpdateCreditApp || shouldUpdateOnlineForm) {
      const customerId = isDashboardAction ? contactIdTask : contact.id
      const data = {
        isCustomerUpdateOnly: true,
        rfpDate: isRfpCustomer ? moment().format('YYYY-MM-DD') : null,
        mfpDate: isMfpCustomer ? moment().format('YYYY-MM-DD') : null,
        creditAppDate: isCreditAppCustomer ? moment().format('YYYY-MM-DD') : null,
        onlineFormDate: isOnlineFormCustomer ? moment().format('YYYY-MM-DD') : null,
        isRfp: isRfpCustomer,
        isMfp: isMfpCustomer,
        isCreditApp: isCreditAppCustomer,
        isOnlineForm: isOnlineFormCustomer,
      }

      updateCustomerLogistics(customerId, divisionId, data)
    }
  }

  const handleCompleteTask = () => {
    handleUpdateCustomerLogistics()

    if (ratingData?.grade || ratingData?.effortLevel) {
      const updateData = {}

      if (ratingData.grade != null) updateData.grade = ratingData.grade
      if (ratingData.effortLevel != null) updateData.effortLevel = ratingData.effortLevel

      if (Object.keys(updateData).length) {
        dispatch(updateContactDivision(divisionId, contactId, updateData))
      }
    }

    completeTask(
      taskId,
      newActivityType,
      // send activity type as activity action [Meeting]  cases
      isCalled ? OUT_GOING_CALL : newActivityType ? newActivityType : null
    )
  }

  let loader = null
  if (isLoading) loader = <Loader />
  const getBody = () => {
    return (
      <>
        <TaskCheckList isComplete taskStatus={taskStatus} />
        <div className='mt-3'>
          {loader}
          <CompleteTaskForm
            openNextStep={openNextStep}
            isOpen={isOpen}
            isRfpCustomer={isRfpCustomer}
            isMfpCustomer={isMfpCustomer}
            toggleIsRfpCustomer={() => setIsRfpCustomer((prev) => !prev)}
            toggleIsMfpCustomer={() => setIsMfpCustomer((prev) => !prev)}
            isCreditAppCustomer={isCreditAppCustomer}
            toggleIsCreditAppCustomer={() => setIsCreditAppCustomer((prev) => !prev)}
            isOnlineFormCustomer={isOnlineFormCustomer}
            toggleIsOnlineFormCustomer={() => setIsOnlineFormCustomer((prev) => !prev)}
            displayButton={displayButton}
            ratingData={ratingData}
            setRatingData={setRatingData}
            contact={contact}
          />
        </div>
        <div className='mt-3'>
          <ContactInfoForTask openNextStep={openNextStep} />
        </div>
        <div className='mt-3 mb-2'>
          <CompleteTaskAdditionalContact openNextStep={openNextStep} />
        </div>
        <div className='mt-3 textAreaHight'>
          <CompleteTaskContactInfo />
        </div>
        <Row className='mt-3'>
          <Col className='text-left'>
            <Button size='sm' className={'btn btn-gold'} onClick={() => closeModal('complete_task')}>
              Cancel
            </Button>
          </Col>

          <Col className='text-right'>
            {divisionId !== LOGISTICS && (
              <Button
                size='sm'
                disabled={taskStatus === 2 || isOpen || isLoading}
                className={'btn btn-gold mr-3'}
                onClick={() => toggleConfirmSendEmailModal()}
              >
                Send Email
              </Button>
            )}
            <Button
              size='sm'
              className={'btn btn-gold'}
              onClick={handleCompleteTask}
              disabled={taskStatus === 2 || isOpen || isLoading}
            >
              Complete
            </Button>
          </Col>
        </Row>
        <Row className={'mt-5'}>
          <Col>{contactId && <ActivitiesTimeline contactId={contactId} showFilters />}</Col>
        </Row>
        <PopupModal
          headerText='Confirm Sending Email'
          isOpen={confirmSendEmailModal}
          toggle={toggleConfirmSendEmailModal}
          className='modal-warning'
          buttons={[
            {
              block: false,
              color: 'warning',
              text: 'Yes',
              onClick: () => {
                completeTask(taskId, newActivityType, EMAIL, true)
                toggleConfirmSendEmailModal()
              },
            },
            {
              block: false,
              color: 'primary',
              text: 'No',
              onClick: () => {
                toggleConfirmSendEmailModal()
              },
            },
          ]}
        >
          <p>
            <strong>Alert! You are sending direct email to Contact.</strong>
          </p>
          <p>
            <strong>Please verity Subject and Notes!</strong>
          </p>
          <p>
            <strong>Are you sure you wish to proceed with email sending?</strong>
          </p>
        </PopupModal>
      </>
    )
  }
  if (!isModal) {
    return getBody()
  }
  return <>{getBody()}</>
}

CompleteTask.propTypes = {
  isModal: PropTypes.bool,
  toggleModal: PropTypes.func,
  isModalToggle: PropTypes.bool,
  contactName: PropTypes.string,
  contactId: PropTypes.number,
  getNotesForTask: PropTypes.func.isRequired,
  nextStepId: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
  completeTask: PropTypes.func.isRequired,
  taskId: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
}

CompleteTask.defaultProps = {
  isModal: true,
  isModalToggle: false,
  toggleModal: () => {},
  contactName: '',
  taskId: false,
  nextStepId: false,
  contactId: 0,
}

const mapStateToProps = (state) => {
  return {
    errors: state.errors,
    contactName: state.Task.contactName,
    contactId: state.Task.data.contactId,
    nextStepId: state.Task.data.nextStepId,
    status: state.Task.data.status,
    isLoading: state.Task.isLoading,
    contact: state.contacts.contact,
    divisionId: state.auth.user.division_id,
    isLogisticsCustomerProspect:
      state.auth.user.division_id === LOGISTICS &&
      state.contacts.contact.contactCategory === SHIPPERS &&
      state.contacts.contact.contactDivisionData.statusId == PROSPECT_STATUS,
    isLogisticsProspectOrAccountCustomer:
      state.auth.user.division_id === LOGISTICS &&
      state.contacts.contact.contactCategory === SHIPPERS &&
      [PROSPECT_STATUS, PENDING_STATUS, ACCOUNT_STATUS].includes(state.contacts.contact.contactDivisionData.statusId),
  }
}

export default connect(mapStateToProps, {
  setModal,
  closeModal,
  completeTask,
  getNotesForTask,
  updateCustomerLogistics,
})(CompleteTask)
